import React, { useEffect, useState, useMemo } from 'react';
import { Plus, Edit2, Trash2, Save, Search, MapPin, ExternalLink, Loader2, ArrowUp, ArrowDown, Eye, EyeOff, Star, Unlink, RefreshCw } from 'lucide-react';
import { adminApi, publicApi } from '../../api';
import { PageHeader, Table, Modal, Field, Input, Textarea, Select, I18nInputs, HoursEditor, ImageUpload } from './AdminUI';
import GoogleDiscoverImport from './GoogleDiscoverImport';

const EMPTY = {
  city_slug: 'desenzano', category_key: 'ristoranti', subcategory: '',
  name: '', image: '', description: {it:'',en:''}, hours: {}, phone: '',
  email: '', whatsapp: '', address: '', latitude: '', longitude: '',
  price_range: '', website: '', external_link: '',
  featured: false, verified: false, premium: false, premium_until: '',
  home_featured: false, home_featured_order: 0,
  sponsor_level: 'none', sponsor_start: '', sponsor_end: '',
  rating: '', review_count: 0,
  status: 'published', order: 0
};

export default function AdminActivities() {
  const [items, setItems] = useState([]);
  const [cities, setCities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState('');
  const [filterCity, setFilterCity] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [filterSub, setFilterSub] = useState('');

  const [filterHomeFeatured, setFilterHomeFeatured] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all'); // 'all' | 'published' | 'draft' | 'archived'
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = () => Promise.all([
    adminApi.activities(), adminApi.cities(), adminApi.categories(), adminApi.subcategories()
  ]).then(([a, ci, ca, s]) => { setItems(a); setCities(ci); setCategories(ca); setSubcategories(s); });
  useEffect(() => { load(); }, []);

  const homeFeaturedCount = useMemo(() => items.filter(a => a.home_featured).length, [items]);
  const draftCount = useMemo(() => items.filter(a => (a.status || 'published') === 'draft').length, [items]);

  const rows = useMemo(() => {
    return items
      .filter(a => {
        if (filterHomeFeatured && !a.home_featured) return false;
        if (filterStatus !== 'all' && (a.status || 'published') !== filterStatus) return false;
        if (filterCity && a.city_slug !== filterCity) return false;
        if (filterCat && a.category_key !== filterCat) return false;
        if (filterSub && (a.subcategory || '') !== filterSub) return false;
        if (q && !`${a.name} ${a.address || ''}`.toLowerCase().includes(q.toLowerCase())) return false;
        return true;
      })
      .sort((a, b) => {
        // When viewing home-featured only, sort by home_featured_order
        if (filterHomeFeatured) {
          return (a.home_featured_order ?? 0) - (b.home_featured_order ?? 0);
        }
        const oa = a.order ?? 0;
        const ob = b.order ?? 0;
        if (oa !== ob) return oa - ob;
        return (a.name || '').localeCompare(b.name || '');
      });
  }, [items, filterCity, filterCat, filterSub, filterHomeFeatured, filterStatus, q]);

  // Subcategories filtered by selected category (if any). Empty category = show all subs.
  const availableSubs = useMemo(() => {
    if (!filterCat) return subcategories;
    return subcategories.filter(s => s.category_key === filterCat);
  }, [filterCat, subcategories]);

  // Auto-reset subcategory filter if the category filter changes AND the current sub doesn't belong
  useEffect(() => {
    if (filterCat && filterSub) {
      const belongs = subcategories.some(s => s.category_key === filterCat && s.key === filterSub);
      if (!belongs) setFilterSub('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterCat]);

  // Existing subcategories present in current filtered rows — for grouped display
  const subsInRows = useMemo(() => {
    if (filterSub) return []; // already single sub selected
    const seen = new Set();
    rows.forEach(a => seen.add(a.subcategory || ''));
    return Array.from(seen);
  }, [rows, filterSub]);

  const [reordering, setReordering] = useState(false);
  const moveRow = async (row, dir) => {
    const idx = rows.findIndex(r => r.id === row.id);
    const neighbourIdx = idx + dir;
    if (neighbourIdx < 0 || neighbourIdx >= rows.length) return;
    const other = rows[neighbourIdx];
    setReordering(true);
    try {
      // Give each row a distinct order value if they collide
      let orderA = row.order ?? 0;
      let orderB = other.order ?? 0;
      if (orderA === orderB) { orderA = idx; orderB = neighbourIdx; }
      await Promise.all([
        adminApi.updateActivity(row.id, { ...row, order: orderB, subcategory: row.subcategory || null,
          latitude: row.latitude ?? null, longitude: row.longitude ?? null }),
        adminApi.updateActivity(other.id, { ...other, order: orderA, subcategory: other.subcategory || null,
          latitude: other.latitude ?? null, longitude: other.longitude ?? null }),
      ]);
      await load();
    } catch (e) {
      alert('Errore riordinamento: ' + (e.response?.data?.detail || e.message));
    } finally { setReordering(false); }
  };

  // Live subcategories for the OPEN modal — falls back to a fresh admin fetch
  // if the initial admin state doesn't include subs for this category (auth
  // glitch, stale state, or the sub is hidden i.e. `active: false`).
  // Using adminApi here (instead of publicApi) so we always see ALL subs the
  // admin has created, including inactive/hidden ones — otherwise you can't
  // assign an activity to a temporarily hidden sub.
  const [liveSubsForModal, setLiveSubsForModal] = useState(null);
  useEffect(() => {
    if (!editing?.category_key) { setLiveSubsForModal(null); return; }
    const fromAdmin = subcategories.filter(s => s.category_key === editing.category_key);
    if (fromAdmin.length > 0) { setLiveSubsForModal(null); return; }
    let cancelled = false;
    adminApi.subcategories()
      .then(list => {
        if (cancelled) return;
        const filtered = Array.isArray(list) ? list.filter(s => s.category_key === editing.category_key) : [];
        setLiveSubsForModal(filtered);
      })
      .catch(() => { if (!cancelled) setLiveSubsForModal([]); });
    return () => { cancelled = true; };
  }, [editing?.category_key, subcategories]);

  const catSubs = editing
    ? (
        (subcategories.filter(s => s.category_key === editing.category_key).length > 0)
          ? subcategories.filter(s => s.category_key === editing.category_key)
          : (liveSubsForModal || [])
      )
    : [];

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...editing,
        order: Number(editing.order) || 0,
        latitude: editing.latitude === '' || editing.latitude == null ? null : Number(editing.latitude),
        longitude: editing.longitude === '' || editing.longitude == null ? null : Number(editing.longitude),
        subcategory: editing.subcategory || null,
        sponsor_level: editing.sponsor_level || 'none',
        sponsor_start: editing.sponsor_start || null,
        sponsor_end: editing.sponsor_end || null,
        premium_until: editing.premium_until || null,
        rating: editing.rating === '' || editing.rating == null ? null : Number(editing.rating),
        review_count: Number(editing.review_count) || 0,
      };
      if (editing.id) await adminApi.updateActivity(editing.id, payload);
      else await adminApi.createActivity(payload);
      setEditing(null); load();
    } catch (err) {
      const d = err.response?.data?.detail;
      let msg;
      if (Array.isArray(d)) {
        // Pydantic validation errors: [{loc: ['body','field'], msg: '...', type: '...'}, ...]
        msg = d.map(e => {
          const field = Array.isArray(e.loc) ? e.loc.filter(x => x !== 'body').join('.') : '';
          return `• ${field ? field + ': ' : ''}${e.msg || e.type || 'invalid'}`;
        }).join('\n');
      } else if (typeof d === 'string') {
        msg = d;
      } else if (d) {
        msg = JSON.stringify(d);
      } else {
        msg = err.message || 'Errore';
      }
      alert('Errore salvataggio attività:\n\n' + msg);
      console.error('Save activity error:', err.response?.data || err);
    }
  };

  const [geoBusy, setGeoBusy] = useState(false);
  const geolocateOne = async () => {
    if (!editing?.address) { alert('Inserisci prima un indirizzo'); return; }
    setGeoBusy(true);
    try {
      const r = await adminApi.geocodeOne(editing.address);
      setEditing({ ...editing, latitude: r.latitude, longitude: r.longitude });
    } catch (e) {
      alert('Impossibile geolocalizzare: ' + (e.response?.data?.detail || e.message));
    } finally { setGeoBusy(false); }
  };

  const del = async (row) => {
    if (!window.confirm(`Eliminare "${row.name}"?`)) return;
    await adminApi.deleteActivity(row.id); load();
  };

  const toggleVisibility = async (row) => {
    await adminApi.toggleActivityVisibility(row.id); load();
  };

  const toggleHomeFeatured = async (row) => {
    // Optimistic UI so the star flips instantly
    setItems(prev => prev.map(a => a.id === row.id ? { ...a, home_featured: !a.home_featured } : a));
    try {
      await adminApi.toggleActivityHomeFeatured(row.id);
      // Refresh to get the assigned home_featured_order
      load();
    } catch (e) {
      // Rollback on error
      setItems(prev => prev.map(a => a.id === row.id ? { ...a, home_featured: !a.home_featured } : a));
      alert('Errore: ' + (e.response?.data?.detail || e.message));
    }
  };

  // ---- Bulk selection (used on the Bozze tab) ----
  const toggleSelect = (id) => setSelectedIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const clearSelection = () => setSelectedIds(new Set());
  // When leaving the Bozze tab, wipe the selection so we don't publish rows the user can't see.
  useEffect(() => { if (filterStatus !== 'draft') clearSelection(); }, [filterStatus]);
  const selectAllVisible = () => setSelectedIds(new Set(rows.map(r => r.id)));

  const bulkPublish = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (!window.confirm(`Pubblicare ${ids.length} bozz${ids.length === 1 ? 'a' : 'e'}? Diventeranno immediatamente visibili al pubblico.`)) return;
    setBulkBusy(true);
    try {
      await adminApi.bulkStatus(ids, 'published');
      clearSelection();
      await load();
    } catch (e) {
      alert('Errore: ' + (e.response?.data?.detail || e.message));
    } finally {
      setBulkBusy(false);
    }
  };

  // ---- Preview URL for a draft (opens the public category page with ?preview=<id>) ----
  const previewUrl = (row) => {
    const cat = row.category_key || 'ristoranti';
    const sub = row.subcategory ? `/${row.subcategory}` : '';
    return `/${row.city_slug}/cosa-vedere/${cat}${sub}?preview=${row.id}#act-${row.id}`;
  };
  const openPreview = (row) => {
    const url = previewUrl(row);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div>
      <PageHeader title="Attività & Aziende" subtitle={`${items.length} attività nel database · ${homeFeaturedCount} in vetrina Home`} action={
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={async () => {
              if (!window.confirm('Riparare le sottocategorie mancanti (noleggi, trasporti, ristoranti)? Sicuro anche se già presenti.')) return;
              try {
                const r = await adminApi.reseedSubcategories();
                alert(`${r.summary}\n\nInserite: ${r.inserted.join(', ') || '(nessuna)'}\nAggiornate: ${r.updated.join(', ') || '(nessuna)'}`);
                load();
              } catch (e) {
                alert('Errore: ' + (e.response?.data?.detail || e.message));
              }
            }}
            data-testid="reseed-subs-btn"
            className="btn-secondary flex items-center gap-2 text-sm"
            title="Ripristina sottocategorie noleggi/trasporti/ristoranti se mancano"
          >
            <RefreshCw size={14}/> Ripara sottocategorie
          </button>
          <GoogleBulkEnrichButton onDone={load} cities={cities} />
          <GoogleDiscoverImport cities={cities} categories={categories} subcategories={subcategories} onImported={load} />
          <button onClick={() => setEditing({...EMPTY})} data-testid="new-activity-btn" className="btn-primary flex items-center gap-2"><Plus size={16}/> Nuova</button>
        </div>
      } />

      {/* Vetrina Home banner — click to filter */}
      <button
        type="button"
        onClick={() => setFilterHomeFeatured(v => !v)}
        data-testid="filter-home-featured"
        className={`w-full mb-4 px-4 py-3 rounded-lg border-2 flex items-center justify-between transition-all ${
          filterHomeFeatured
            ? 'border-indigo-500 bg-indigo-50 text-indigo-900'
            : 'border-indigo-200 bg-gradient-to-r from-indigo-50/50 to-purple-50/50 text-indigo-800 hover:border-indigo-300'
        }`}
      >
        <span className="flex items-center gap-2 font-semibold">
          <Star size={16} className={filterHomeFeatured ? 'fill-indigo-600 text-indigo-600' : 'text-indigo-500'} />
          ✨ Vetrina Home — Esperienze da non perdere
          <span className="text-xs font-mono bg-white px-2 py-0.5 rounded-full border border-indigo-200">
            {homeFeaturedCount} attiv{homeFeaturedCount === 1 ? 'a' : 'e'} in vetrina
          </span>
        </span>
        <span className="text-xs">{filterHomeFeatured ? '× Rimuovi filtro' : 'Filtra solo quelle in vetrina →'}</span>
      </button>

      {/* Status tabs — Tutte / Pubblicate / Bozze / Archiviate */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="status-tabs">
        {[
          { key: 'all', label: 'Tutte', color: 'slate' },
          { key: 'published', label: 'Pubblicate', color: 'emerald' },
          { key: 'draft', label: `Bozze${draftCount ? ` (${draftCount})` : ''}`, color: 'amber' },
          { key: 'archived', label: 'Archiviate', color: 'zinc' },
        ].map(t => {
          const active = filterStatus === t.key;
          const isDraft = t.key === 'draft';
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setFilterStatus(t.key)}
              data-testid={`status-tab-${t.key}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                active
                  ? (t.color === 'amber' ? 'bg-amber-500 border-amber-500 text-white' :
                     t.color === 'emerald' ? 'bg-emerald-600 border-emerald-600 text-white' :
                     t.color === 'zinc' ? 'bg-zinc-700 border-zinc-700 text-white' :
                     'bg-slate-800 border-slate-800 text-white')
                  : `bg-white text-slate-700 border-slate-300 hover:border-slate-500 ${isDraft && draftCount ? 'ring-2 ring-amber-200' : ''}`
              }`}
            >
              {t.label}
            </button>
          );
        })}
        {filterStatus === 'draft' && draftCount > 0 && (
          <span className="text-xs text-amber-700 self-center ml-1">
            💡 Rivedi e pubblica queste bozze importate da Google.
          </span>
        )}
      </div>

      {/* Bulk actions toolbar — visible only on the Bozze tab */}
      {filterStatus === 'draft' && (
        <div className="mb-4 flex flex-wrap items-center gap-2 p-3 rounded-lg border border-amber-200 bg-amber-50/60" data-testid="bulk-toolbar">
          <span className="text-sm text-amber-900">
            <strong>{selectedIds.size}</strong> selezionate su {rows.length} bozze
          </span>
          <div className="flex-1" />
          <button
            type="button"
            onClick={selectAllVisible}
            disabled={rows.length === 0}
            data-testid="bulk-select-all"
            className="px-3 py-1.5 text-xs rounded-md border border-amber-300 bg-white hover:bg-amber-100 text-amber-800 disabled:opacity-50"
          >
            Seleziona tutte ({rows.length})
          </button>
          <button
            type="button"
            onClick={clearSelection}
            disabled={selectedIds.size === 0}
            data-testid="bulk-clear"
            className="px-3 py-1.5 text-xs rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 disabled:opacity-50"
          >
            Deseleziona
          </button>
          <button
            type="button"
            onClick={bulkPublish}
            disabled={bulkBusy || selectedIds.size === 0}
            data-testid="bulk-publish-btn"
            className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-semibold disabled:opacity-50"
          >
            {bulkBusy ? <Loader2 size={14} className="animate-spin"/> : <Eye size={14}/>}
            Pubblica selezionate{selectedIds.size ? ` (${selectedIds.size})` : ''}
          </button>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-3 text-gray-400" />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Cerca per nome o indirizzo..." className="pl-9" />
        </div>
        <Select value={filterCity} onChange={e => setFilterCity(e.target.value)} className="sm:max-w-xs">
          <option value="">Tutte le città</option>
          {cities.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
        </Select>
        <Select value={filterCat} onChange={e => setFilterCat(e.target.value)} className="sm:max-w-xs">
          <option value="">Tutte le categorie</option>
          {categories.map(c => <option key={c.key} value={c.key}>{c.label?.it || c.key}</option>)}
        </Select>
        <Select value={filterSub} onChange={e => setFilterSub(e.target.value)} className="sm:max-w-xs" data-testid="filter-subcategory">
          <option value="">Tutte le sottocategorie</option>
          {availableSubs.map(s => (
            <option key={`${s.category_key}-${s.key}`} value={s.key}>
              {(s.label?.it || s.key)}{!filterCat && ` (${s.category_key})`}
            </option>
          ))}
        </Select>
      </div>

      {/* Info card when filtered — clarifies drag&drop scope */}
      {(filterCity || filterCat || filterSub) && (
        <div className="mb-4 px-4 py-2.5 rounded-lg bg-blue-50 border border-blue-200 text-blue-800 text-sm inline-flex items-start gap-2 flex-wrap" data-testid="reorder-scope-hint">
          <span className="font-semibold">Riordino attivo su:</span>
          {filterCity && <span className="px-2 py-0.5 rounded bg-white text-xs border border-blue-200">📍 {cities.find(c => c.slug === filterCity)?.name || filterCity}</span>}
          {filterCat && <span className="px-2 py-0.5 rounded bg-white text-xs border border-blue-200">🏷 {categories.find(c => c.key === filterCat)?.label?.it || filterCat}</span>}
          {filterSub && <span className="px-2 py-0.5 rounded bg-white text-xs border border-blue-200">↳ {availableSubs.find(s => s.key === filterSub)?.label?.it || filterSub}</span>}
          <span className="text-xs text-blue-600">— trascina ⋮⋮ per ordinare {rows.length} risultati</span>
        </div>
      )}

      <Table
        draggable
        onReorder={async (next) => {
          setItems(prev => {
            // Merge reordered subset into full items list (preserve items not in current filter)
            const nextIds = next.map(x => x.id);
            const others = prev.filter(x => !nextIds.includes(x.id));
            return [...next, ...others];
          });
          try {
            await adminApi.reorderActivities(next.map(x => x.id));
          } catch (e) { alert('Errore riordino: ' + (e.response?.data?.detail || e.message)); load(); }
        }}
        columns={[
          { key: 'name', label: 'Nome', render: r => (
            <div className="flex items-start gap-3">
              {filterStatus === 'draft' && (
                <input
                  type="checkbox"
                  checked={selectedIds.has(r.id)}
                  onChange={() => toggleSelect(r.id)}
                  onClick={e => e.stopPropagation()}
                  data-testid={`draft-check-${r.id}`}
                  className="mt-1 h-4 w-4 accent-emerald-600 flex-shrink-0"
                  aria-label={`Seleziona ${r.name}`}
                />
              )}
              {r.image && <img src={r.image} alt="" className="w-14 h-10 object-cover rounded flex-shrink-0" />}
              <div className="min-w-0">
                <div className="font-semibold flex items-center gap-2 flex-wrap">
                  {r.name}
                  {r.verified && <span title="Verificata" className="text-emerald-600 text-xs">✓</span>}
                  {r.status === 'draft' && <span className="text-xs bg-amber-100 text-amber-700 px-1.5 rounded">bozza</span>}
                  {r.status === 'archived' && <span className="text-xs bg-gray-200 text-gray-600 px-1.5 rounded">archiviata</span>}
                </div>
                {r.address && <div className="text-xs text-gray-500">{r.address}</div>}
                {(typeof r.latitude === 'number') ? (
                  <div className="text-xs text-emerald-600 flex items-center gap-1"><MapPin size={11}/> {r.latitude.toFixed(4)}, {r.longitude.toFixed(4)}</div>
                ) : (
                  <div className="text-xs text-red-500">Non geolocalizzata</div>
                )}
                {r.hours && Object.keys(r.hours).length > 0 && (
                  r.hours_source === 'google' ? (
                    <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200" title="Orari sincronizzati automaticamente da Google" data-testid={`hours-badge-${r.id}`}>
                      <MapPin size={9}/> Orari da Google
                    </div>
                  ) : r.hours_source === 'manual' ? (
                    <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200" title="Orari modificati manualmente — protetti dal risync automatico" data-testid={`hours-badge-${r.id}`}>
                      <Edit2 size={9}/> Orari manuali
                    </div>
                  ) : null
                )}
                {/* Photo source: manual (own upload) vs. google fallback */}
                {(() => {
                  const hasManual = !!r.image || (Array.isArray(r.gallery) && r.gallery.length > 0);
                  if (hasManual) return (
                    <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200" title="Foto caricate manualmente" data-testid={`photos-badge-${r.id}`}>
                      <Edit2 size={9}/> Foto manuali
                    </div>
                  );
                  const hasGoogle = Array.isArray(r.google_photos) && r.google_photos.length > 0 && !r.google_photos_disabled;
                  if (hasGoogle) return (
                    <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200" title={`Fallback automatico: ${r.google_photos.length} foto da Google`} data-testid={`photos-badge-${r.id}`}>
                      <MapPin size={9}/> Foto da Google ({r.google_photos.length})
                    </div>
                  );
                  return null;
                })()}
              </div>
            </div>
          ) },
          { key: 'city_slug', label: 'Città' },
          { key: 'category_key', label: 'Categoria', render: r => <span className="text-xs font-mono bg-gray-100 px-2 py-0.5 rounded">{r.category_key}{r.subcategory ? ` / ${r.subcategory}` : ''}</span> },
          { key: 'phone', label: 'Telefono' }
        ]}
        rows={rows}
        actions={row => {
          const idx = rows.findIndex(r => r.id === row.id);
          return (
          <div className="flex gap-1 justify-end items-center">
            <button
              onClick={() => moveRow(row, -1)}
              disabled={reordering || idx <= 0}
              title="Sposta su"
              className="p-1.5 hover:bg-gray-100 rounded text-gray-500 disabled:opacity-30"
              data-testid={`move-up-${row.id}`}
            ><ArrowUp size={14}/></button>
            <button
              onClick={() => moveRow(row, 1)}
              disabled={reordering || idx >= rows.length - 1}
              title="Sposta giù"
              className="p-1.5 hover:bg-gray-100 rounded text-gray-500 disabled:opacity-30"
              data-testid={`move-down-${row.id}`}
            ><ArrowDown size={14}/></button>
            <button
              onClick={() => toggleHomeFeatured(row)}
              data-testid={`home-featured-toggle-${row.id}`}
              title={row.home_featured ? 'Rimuovi dalla Vetrina Home' : 'Aggiungi alla Vetrina Home (carosello homepage)'}
              className={`p-1.5 hover:bg-indigo-50 rounded transition-colors ${row.home_featured ? 'text-indigo-600' : 'text-gray-300 hover:text-indigo-400'}`}
            >
              <Star size={16} className={row.home_featured ? 'fill-indigo-500' : ''} />
            </button>
            <button onClick={() => setEditing({...row, description: row.description || {}, hours: row.hours || {}, latitude: row.latitude ?? '', longitude: row.longitude ?? ''})} className="p-1.5 hover:bg-gray-100 rounded text-blue-600"><Edit2 size={15}/></button>
            <button
              onClick={() => openPreview(row)}
              data-testid={`act-preview-${row.id}`}
              title={row.status === 'draft' ? 'Anteprima bozza (non pubblica) in nuova scheda' : 'Apri sulla pagina pubblica'}
              className="p-1.5 hover:bg-gray-100 rounded text-slate-500 hover:text-slate-800"
            >
              <ExternalLink size={15}/>
            </button>
            <button
              onClick={() => toggleVisibility(row)}
              data-testid={`act-toggle-${row.id}`}
              title={(row.status || 'published') === 'published' ? 'Nascondi (bozza)' : 'Rendi visibile'}
              className={`p-1.5 hover:bg-gray-100 rounded ${(row.status || 'published') === 'published' ? 'text-green-600' : 'text-gray-400'}`}
            >
              {(row.status || 'published') === 'published' ? <Eye size={15}/> : <EyeOff size={15}/>}
            </button>
            <button onClick={() => del(row)} className="p-1.5 hover:bg-gray-100 rounded text-red-600"><Trash2 size={15}/></button>
          </div>
        );}}
      />

      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing?.id ? 'Modifica attività' : 'Nuova attività'} maxWidth="max-w-4xl">
        {editing && (
          <form onSubmit={save}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Città">
                <Select required value={editing.city_slug} onChange={e => setEditing({...editing, city_slug: e.target.value})}>
                  {cities.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </Select>
              </Field>
              <Field label="Categoria">
                <Select required value={editing.category_key} onChange={e => setEditing({...editing, category_key: e.target.value, subcategory: ''})}>
                  {categories.map(c => <option key={c.key} value={c.key}>{c.label?.it || c.key}</option>)}
                </Select>
              </Field>
              <Field label="Sottocategoria">
                <Select value={editing.subcategory || ''} onChange={e => setEditing({...editing, subcategory: e.target.value})}>
                  <option value="">(nessuna)</option>
                  {catSubs.map(s => <option key={s.key} value={s.key}>{s.label?.it || s.key}</option>)}
                </Select>
              </Field>
            </div>
            <Field label="Nome attività"><Input required value={editing.name} onChange={e => setEditing({...editing, name: e.target.value})} /></Field>

            <Field label="Anche visibile nelle città (opzionale)"
                   hint="Se l'attività è al confine tra più località, spunta le città in cui dev'essere mostrata oltre alla principale.">
              <div className="flex flex-wrap gap-2" data-testid="also-visible-in-selector">
                {cities.filter(c => c.slug !== editing.city_slug).map(c => {
                  const active = (editing.also_visible_in || []).includes(c.slug);
                  return (
                    <button
                      type="button"
                      key={c.slug}
                      onClick={() => setEditing({
                        ...editing,
                        also_visible_in: active
                          ? (editing.also_visible_in || []).filter(s => s !== c.slug)
                          : [...(editing.also_visible_in || []), c.slug]
                      })}
                      data-testid={`also-visible-${c.slug}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                        active
                          ? 'bg-[color:var(--yup-primary)] border-[color:var(--yup-primary)] text-white'
                          : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'
                      }`}
                    >
                      {active && '✓ '}{c.name}
                    </button>
                  );
                })}
                {cities.filter(c => c.slug !== editing.city_slug).length === 0 && (
                  <span className="text-xs text-gray-400">Nessuna altra città disponibile</span>
                )}
              </div>
            </Field>
            <Field label="Descrizione (per lingua)"><I18nInputs kind="textarea" value={editing.description} onChange={v => setEditing({...editing, description: v})} /></Field>
            <Field label="Immagine attività"><ImageUpload value={editing.image} onChange={v => setEditing({...editing, image: v})} hint="Trascina un file o incolla un URL" /></Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Telefono"><Input value={editing.phone || ''} onChange={e => setEditing({...editing, phone: e.target.value})} placeholder="+39 030 ..." /></Field>
              <Field label="Email"><Input type="email" value={editing.email || ''} onChange={e => setEditing({...editing, email: e.target.value})} placeholder="info@..." /></Field>
              <Field label="WhatsApp"><Input value={editing.whatsapp || ''} onChange={e => setEditing({...editing, whatsapp: e.target.value})} placeholder="+39 333 ..." /></Field>
              <Field label="Indirizzo"><Input value={editing.address || ''} onChange={e => setEditing({...editing, address: e.target.value})} placeholder="Via ..., Città" /></Field>
              <Field label="Fascia prezzo"><Input value={editing.price_range || ''} onChange={e => setEditing({...editing, price_range: e.target.value})} placeholder="€ / €€ / €€€" /></Field>
              <Field label="Sito web"><Input value={editing.website || ''} onChange={e => setEditing({...editing, website: e.target.value})} placeholder="https://..." /></Field>
              <Field label="Link esterno (Google Maps, prenotazione, ecc.)"><Input value={editing.external_link || ''} onChange={e => setEditing({...editing, external_link: e.target.value})} placeholder="https://..." /></Field>
              <Field label="Stato pubblicazione">
                <Select value={editing.status || 'published'} onChange={e => setEditing({...editing, status: e.target.value})}>
                  <option value="draft">Bozza</option>
                  <option value="published">Pubblicata</option>
                  <option value="archived">Archiviata</option>
                </Select>
              </Field>
            </div>

            <div className="mt-4 border border-emerald-200 bg-emerald-50 rounded p-3">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div className="font-medium text-emerald-900 flex items-center gap-2">
                  <MapPin size={16}/> Coordinate GPS
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={geolocateOne} disabled={geoBusy || !editing.address}
                          className="text-xs px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded disabled:opacity-50 inline-flex items-center gap-1.5"
                          data-testid="activity-geolocate-btn">
                    {geoBusy ? <Loader2 size={12} className="animate-spin"/> : <MapPin size={12}/>}
                    Geolocalizza da indirizzo
                  </button>
                  {editing.latitude && editing.longitude && (
                    <a target="_blank" rel="noopener noreferrer"
                       href={`https://www.google.com/maps?q=${editing.latitude},${editing.longitude}`}
                       className="text-xs px-3 py-1.5 bg-white border border-emerald-300 hover:bg-emerald-100 text-emerald-800 rounded inline-flex items-center gap-1.5">
                      <ExternalLink size={12}/> Verifica su Maps
                    </a>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Latitudine">
                  <Input type="number" step="0.0000001" value={editing.latitude ?? ''}
                         onChange={e => setEditing({...editing, latitude: e.target.value})}
                         placeholder="45.469..."
                         data-testid="activity-latitude-input" />
                </Field>
                <Field label="Longitudine">
                  <Input type="number" step="0.0000001" value={editing.longitude ?? ''}
                         onChange={e => setEditing({...editing, longitude: e.target.value})}
                         placeholder="10.541..."
                         data-testid="activity-longitude-input" />
                </Field>
              </div>
              <p className="text-xs text-emerald-700 mt-2">
                💡 Suggerimento: clicca <strong>Geolocalizza da indirizzo</strong> per calcolare le coordinate automatiche via OpenStreetMap, oppure incolla manualmente lat/lng copiati da Google Maps (click destro → "Cosa c'è qui?").
              </p>
            </div>

            <Field label={
              <span className="flex items-center gap-2 flex-wrap">
                <span>Orari (lascia vuoto se non applicabile)</span>
                {editing.hours_source === 'google' && (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 border border-blue-200"
                    title="Gli orari sono sincronizzati automaticamente dagli orari pubblici di Google"
                    data-testid="hours-source-badge"
                  >
                    <MapPin size={10}/> Sincronizzati da Google
                  </span>
                )}
                {editing.hours_source === 'manual' && (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200"
                    title="Orari modificati manualmente — non verranno sovrascritti dalla sincronizzazione automatica di Google"
                    data-testid="hours-source-badge"
                  >
                    <Edit2 size={10}/> Modificati manualmente
                  </span>
                )}
              </span>
            }>
              <HoursEditor value={editing.hours} onChange={v => setEditing({...editing, hours: v, hours_source: 'manual'})} />
            </Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Ordine"><Input type="number" value={editing.order} onChange={e => setEditing({...editing, order: e.target.value})} /></Field>
              <Field label="In evidenza">
                <label className="flex items-center gap-2 pt-2"><input type="checkbox" checked={!!editing.featured} onChange={e => setEditing({...editing, featured: e.target.checked})}/> Attività in evidenza</label>
              </Field>
              <Field label="Verificata">
                <label className="flex items-center gap-2 pt-2"><input type="checkbox" checked={!!editing.verified} onChange={e => setEditing({...editing, verified: e.target.checked})}/> Contenuto verificato</label>
              </Field>
            </div>

            {/* HOME VETRINA — Featured carousel on Home ("Esperienze da non perdere") */}
            <div className="mt-4 rounded-lg border-2 border-indigo-300 bg-gradient-to-br from-indigo-50 to-purple-50 p-4" data-testid="activity-home-featured-card">
              <div className="flex items-center gap-2 font-semibold text-indigo-900 mb-2">
                ✨ Vetrina Home — Esperienze da non perdere
              </div>
              <p className="text-xs text-indigo-800/80 mb-3">
                Attiva per mostrare questa attività nel carosello "Esperienze da non perdere" della homepage. Fino a 8 attività verranno mostrate, ordinate secondo il valore qui sotto (asc: 0, 10, 20…).
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Mostra sulla Home">
                  <label className="flex items-center gap-2 pt-2">
                    <input
                      type="checkbox"
                      checked={!!editing.home_featured}
                      onChange={e => setEditing({...editing, home_featured: e.target.checked})}
                      data-testid="activity-home-featured-toggle"
                    />
                    <span className="text-sm">In vetrina Home</span>
                  </label>
                </Field>
                <Field label="Ordine nel carosello (asc)">
                  <Input
                    type="number"
                    value={editing.home_featured_order ?? 0}
                    onChange={e => setEditing({...editing, home_featured_order: Number(e.target.value) || 0})}
                    disabled={!editing.home_featured}
                    data-testid="activity-home-featured-order"
                  />
                </Field>
              </div>
            </div>


            {/* SPONSOR LEVEL (paid position + badge) */}
            <div className="mt-4 rounded-lg border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-yellow-50 p-4" data-testid="sponsor-section">
              <div className="flex items-center gap-2 font-semibold text-amber-900 mb-2">
                ⭐ Sponsorizzazione
              </div>
              <p className="text-xs text-amber-800/80 mb-3">
                Assegna un livello sponsor per posizionare l'attività in cima e mostrarle un badge distintivo. Con date di inizio/fine la scadenza è automatica.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label="Livello">
                  <Select
                    value={editing.sponsor_level || 'none'}
                    onChange={e => setEditing({...editing, sponsor_level: e.target.value})}
                    data-testid="activity-sponsor-level-select"
                  >
                    <option value="none">Nessuno</option>
                    <option value="featured">In evidenza</option>
                    <option value="top3">Top 3 (badge 1°/2°/3°)</option>
                    <option value="recommended">Consigliato da YUP (nastro oro)</option>
                  </Select>
                </Field>
                <Field label="Data inizio">
                  <Input
                    type="datetime-local"
                    value={editing.sponsor_start ? String(editing.sponsor_start).slice(0, 16) : ''}
                    onChange={e => setEditing({...editing, sponsor_start: e.target.value ? new Date(e.target.value).toISOString() : ''})}
                    disabled={editing.sponsor_level === 'none' || !editing.sponsor_level}
                    data-testid="activity-sponsor-start-input"
                  />
                </Field>
                <Field label="Data fine (scadenza)">
                  <Input
                    type="datetime-local"
                    value={editing.sponsor_end ? String(editing.sponsor_end).slice(0, 16) : ''}
                    onChange={e => setEditing({...editing, sponsor_end: e.target.value ? new Date(e.target.value).toISOString() : ''})}
                    disabled={editing.sponsor_level === 'none' || !editing.sponsor_level}
                    data-testid="activity-sponsor-end-input"
                  />
                </Field>
              </div>
              <p className="text-[11px] text-amber-800/70 mt-2">
                Lascia le date vuote per sponsorizzazione permanente (fino a rimozione manuale).
                Top 3: solo le 3 più recenti (per data inizio) ricevono il badge numerato, le altre restano "In evidenza".
              </p>
            </div>

            {/* GOOGLE PLACES — rating/recensioni auto */}
            <GooglePlacesPanel
              activity={editing}
              onUpdate={(patch) => setEditing({ ...editing, ...patch })}
            />

            <div className="flex justify-end gap-2 mt-6">
              <button type="button" onClick={() => setEditing(null)} className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">Annulla</button>
              <button type="submit" className="btn-primary flex items-center gap-2"><Save size={16}/> Salva</button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}

// ============ GOOGLE PLACES PANEL ============
function GooglePlacesPanel({ activity, onUpdate }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const linked = !!activity?.google_place_id;

  const doSearch = async () => {
    setError(''); setBusy(true); setResults([]);
    try {
      const q = query || `${activity.name} ${activity.city_slug || ''}`;
      const r = await adminApi.googleSearch(activity.id || 'temp', q);
      setResults(r.results || []);
      if ((r.results || []).length === 0) setError('Nessun risultato trovato — prova con un nome più preciso');
    } catch (e) {
      setError(e.response?.data?.detail || 'Errore ricerca Google');
    } finally { setBusy(false); }
  };

  const doLink = async (place_id) => {
    if (!activity.id) { alert('Salva prima l\'attività, poi collega Google'); return; }
    setBusy(true); setError('');
    try {
      const r = await adminApi.googleLink(activity.id, place_id);
      onUpdate(r.linked || {});
      setResults([]);
    } catch (e) {
      setError(e.response?.data?.detail || 'Errore collegamento');
    } finally { setBusy(false); }
  };

  const doRefresh = async (force = false) => {
    setBusy(true); setError('');
    try {
      const r = await adminApi.googleRefresh(activity.id, force);
      const patch = { google_rating: r.rating, google_review_count: r.review_count, google_last_synced: new Date().toISOString() };
      if (r.hours_updated && r.hours) {
        patch.hours = r.hours;
        patch.hours_source = r.hours_source || 'google';
      }
      if (r.photos_refreshed) {
        // Photos metadata refresh is transparent — parent doesn't need to render photos here,
        // it's the public site that uses google_photos as fallback.
      }
      onUpdate(patch);
    } catch (e) {
      setError(e.response?.data?.detail || 'Errore aggiornamento');
    } finally { setBusy(false); }
  };

  const doUnlink = async () => {
    if (!window.confirm('Scollegare da Google?')) return;
    await adminApi.googleUnlink(activity.id);
    onUpdate({ google_place_id: null, google_rating: null, google_review_count: null });
  };

  return (
    <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-4" data-testid="google-places-panel">
      <div className="flex items-center gap-2 font-semibold text-blue-900 mb-2">
        <MapPin size={16} className="text-blue-700"/> Recensioni Google (automatiche)
      </div>

      {linked ? (
        <div className="rounded-md bg-white border border-blue-200 p-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1 text-amber-500">
              <Star size={18} fill="currentColor"/>
              <span className="text-base font-bold text-slate-800">{activity.google_rating?.toFixed(1) || '—'}</span>
            </div>
            <div className="text-sm text-slate-600">
              <strong>{activity.google_review_count ?? 0}</strong> recensioni Google
            </div>
            <div className="text-xs text-slate-400 ml-auto">
              {activity.google_last_synced ? `Sync: ${new Date(activity.google_last_synced).toLocaleString('it-IT')}` : 'Non sincronizzato'}
            </div>
          </div>
          <div className="mt-2 text-[11px] text-slate-500 break-all">
            place_id: <code className="bg-slate-100 rounded px-1">{activity.google_place_id}</code>
          </div>
          {/* Photo fallback control (Feb 2026) */}
          {(activity.google_photos?.length || 0) > 0 && (
            <div className="mt-3 pt-3 border-t border-blue-100">
              <label className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!activity.google_photos_disabled}
                  onChange={(e) => onUpdate({ google_photos_disabled: e.target.checked })}
                  data-testid="google-photos-disabled-toggle"
                  className="mt-0.5"
                />
                <span>
                  <strong>Disattiva foto Google</strong> per questa attività
                  <span className="block text-[10px] text-slate-500 mt-0.5">
                    Se le foto Google non sono adatte, spuntalo. Il sito non mostrerà il fallback fino a quando non ricaricherai foto manuali. ({activity.google_photos.length} foto disponibili da Google)
                  </span>
                </span>
              </label>
            </div>
          )}
          <div className="flex gap-2 mt-3 flex-wrap">
            <button type="button" onClick={() => doRefresh(false)} disabled={busy} data-testid="google-refresh-btn"
                    className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
              {busy ? <Loader2 size={12} className="animate-spin"/> : <RefreshCw size={12}/>} Aggiorna
            </button>
            {activity.hours_source === 'manual' && (
              <button
                type="button"
                onClick={() => {
                  if (window.confirm('Sovrascrivere gli orari modificati manualmente con quelli attuali di Google?')) {
                    doRefresh(true);
                  }
                }}
                disabled={busy}
                data-testid="google-force-hours-btn"
                className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50"
                title="Ignora la modifica manuale e riscarica gli orari da Google"
              >
                <RefreshCw size={12}/> Forza risync orari
              </button>
            )}
            <button type="button" onClick={doUnlink} disabled={busy} data-testid="google-unlink-btn"
                    className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md bg-white border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-50">
              <Unlink size={12}/> Scollega
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="text-xs text-blue-800/80 mb-2">
            Cerca l'attività su Google Maps e collegala. Il rating e il numero di recensioni saranno importati automaticamente e aggiornati periodicamente.
          </div>
          {!activity.id ? (
            <div className="rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-xs p-2">
              Salva prima l'attività, poi torna qui per collegare Google.
            </div>
          ) : (
            <>
              <div className="flex gap-2">
                <Input
                  placeholder={`Es: ${activity.name || 'Nome attività'} ${activity.city_slug || ''}`}
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  data-testid="google-search-input"
                />
                <button type="button" onClick={doSearch} disabled={busy} data-testid="google-search-btn"
                        className="inline-flex items-center gap-1 px-3 py-2 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-50 flex-shrink-0">
                  {busy ? <Loader2 size={14} className="animate-spin"/> : <Search size={14}/>} Cerca
                </button>
              </div>
              {results.length > 0 && (
                <div className="mt-3 space-y-2 max-h-64 overflow-y-auto">
                  {results.map(r => (
                    <div key={r.place_id} data-testid={`google-result-${r.place_id}`}
                         className="flex items-start gap-3 rounded-md border border-blue-200 bg-white p-2 hover:bg-blue-100/40 transition">
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-800 text-sm truncate">{r.name}</div>
                        <div className="text-xs text-slate-500 truncate">{r.address}</div>
                        <div className="text-xs text-slate-600 mt-0.5 flex items-center gap-2">
                          {r.rating != null && (
                            <span className="inline-flex items-center gap-0.5 text-amber-600">
                              <Star size={10} fill="currentColor"/> {r.rating?.toFixed(1)} ({r.review_count || 0})
                            </span>
                          )}
                        </div>
                      </div>
                      <button type="button" onClick={() => doLink(r.place_id)} disabled={busy}
                              data-testid={`google-link-btn-${r.place_id}`}
                              className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 flex-shrink-0">
                        Collega
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}
      {error && <div className="mt-2 text-xs text-red-600" data-testid="google-error">{error}</div>}
    </div>
  );
}


// ============ BULK ENRICH BUTTON ============
function GoogleBulkEnrichButton({ onDone, cities }) {
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [preflightOpen, setPreflightOpen] = useState(false);
  const [limitInput, setLimitInput] = useState('');   // '' = tutte
  const [cityInput, setCityInput] = useState('');     // '' = tutte

  const openPreflight = () => {
    setLimitInput('');
    setCityInput('');
    setPreflightOpen(true);
  };

  const start = async () => {
    setPreflightOpen(false);
    setBusy(true);
    try {
      const opts = {};
      const n = parseInt(limitInput, 10);
      if (limitInput && !isNaN(n) && n > 0) opts.limit = n;
      if (cityInput) opts.city = cityInput;
      const r = await adminApi.googleBulkEnrich(opts);
      setJob({ id: r.job_id, status: 'running', processed: 0, total: 0, linked: 0, skipped: 0, errors: [], limit: r.limit, city: r.city });
      setOpen(true);
      pollJob(r.job_id);
    } catch (e) {
      alert('Errore: ' + (e.response?.data?.detail || e.message));
      setBusy(false);
    }
  };

  const pollJob = async (jobId) => {
    try {
      const j = await adminApi.googleBulkStatus(jobId);
      setJob({ ...j, id: jobId });
      if (j.status === 'running') setTimeout(() => pollJob(jobId), 1500);
      else { setBusy(false); if (onDone) onDone(); }
    } catch (e) { setBusy(false); }
  };

  const pct = job && job.total > 0 ? Math.round((job.processed / job.total) * 100) : 0;

  return (
    <>
      <button type="button" onClick={openPreflight} disabled={busy} data-testid="google-bulk-enrich-btn"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-blue-50 border border-blue-300 text-blue-700 hover:bg-blue-100 text-sm font-medium disabled:opacity-60">
        {busy ? <Loader2 size={14} className="animate-spin"/> : <MapPin size={14}/>}
        Collega Google (bulk)
      </button>

      {/* PRE-FLIGHT MODAL: limit + city (both optional; empty = all) */}
      {preflightOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setPreflightOpen(false)}>
          <div className="w-full max-w-md rounded-xl bg-white p-6 border border-blue-200" onClick={e => e.stopPropagation()}>
            <div className="font-bold text-slate-800 mb-1 flex items-center gap-2">
              <MapPin size={18} className="text-blue-600"/> Collega Google (bulk)
            </div>
            <p className="text-xs text-slate-600 mb-4">
              Il processo elabora solo le attività ancora <strong>non collegate</strong> a Google. Non sovrascrive foto, orari o campi già editati manualmente.
            </p>
            <div className="space-y-3 mb-4">
              <label className="block">
                <span className="text-xs font-semibold text-slate-700">Limite <span className="text-slate-400">(vuoto = tutte)</span></span>
                <input
                  type="number"
                  min="1"
                  value={limitInput}
                  onChange={e => setLimitInput(e.target.value)}
                  placeholder="es. 10 per fare prima un test"
                  data-testid="bulk-limit-input"
                  className="mt-1 w-full text-sm px-3 py-2 rounded-md border border-slate-300 focus:border-blue-500 focus:ring focus:ring-blue-100 focus:outline-none"
                />
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-slate-700">Solo città <span className="text-slate-400">(vuoto = tutte)</span></span>
                <select
                  value={cityInput}
                  onChange={e => setCityInput(e.target.value)}
                  data-testid="bulk-city-input"
                  className="mt-1 w-full text-sm px-3 py-2 rounded-md border border-slate-300 focus:border-blue-500 focus:ring focus:ring-blue-100 focus:outline-none bg-white"
                >
                  <option value="">Tutte le città</option>
                  {(cities || []).map(c => (
                    <option key={c.slug} value={c.slug}>{c.name || c.slug}</option>
                  ))}
                </select>
              </label>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPreflightOpen(false)}
                className="px-3 py-2 text-sm rounded-md border border-slate-300 hover:bg-slate-50 text-slate-700"
              >
                Annulla
              </button>
              <button
                type="button"
                onClick={start}
                data-testid="bulk-confirm-btn"
                className="px-4 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 font-semibold"
              >
                Avvia
              </button>
            </div>
          </div>
        </div>
      )}
      {open && job && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => job.status !== 'running' && setOpen(false)}>
          <div className="w-full max-w-md rounded-xl bg-white p-6 border border-blue-200" onClick={e => e.stopPropagation()}>
            <div className="font-bold text-slate-800 mb-2 flex items-center gap-2">
              <MapPin size={18} className="text-blue-600"/> Arricchimento Google Maps
            </div>
            {(job.limit || job.city) && (
              <div className="text-[11px] text-slate-500 mb-2">
                Filtri: {job.limit ? `max ${job.limit} attività` : 'nessun limite'}{job.city ? ` · città: ${job.city}` : ''}
              </div>
            )}
            <div className="text-sm text-slate-600 mb-3">
              {job.status === 'running' ? `Elaborazione in corso...` : job.status === 'completed' ? '✅ Completato' : '❌ Errore'}
            </div>
            <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden mb-2">
              <div className="h-full bg-blue-500 transition-all" style={{width: `${pct}%`}}/>
            </div>
            <div className="text-xs text-slate-500 mb-3">
              {job.processed} / {job.total} attività elaborate ({pct}%)
            </div>
            <div className="grid grid-cols-3 gap-2 mb-3 text-center">
              <div className="rounded-md bg-emerald-50 border border-emerald-200 p-2">
                <div className="text-xl font-bold text-emerald-700">{job.linked}</div>
                <div className="text-[10px] text-emerald-800 uppercase">Collegate</div>
              </div>
              <div className="rounded-md bg-amber-50 border border-amber-200 p-2">
                <div className="text-xl font-bold text-amber-700">{job.skipped}</div>
                <div className="text-[10px] text-amber-800 uppercase">Nessun match</div>
              </div>
              <div className="rounded-md bg-red-50 border border-red-200 p-2">
                <div className="text-xl font-bold text-red-700">{(job.errors || []).length}</div>
                <div className="text-[10px] text-red-800 uppercase">Errori</div>
              </div>
            </div>
            {job.status !== 'running' && (
              <button onClick={() => setOpen(false)} className="w-full px-4 py-2 rounded-md bg-blue-600 text-white text-sm">Chiudi</button>
            )}
          </div>
        </div>
      )}
    </>
  );
}
